import { Component, Input, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { State } from '@k12/forms';
import { CountiesService, CountyResponse } from '@k12/school-services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,

  selector: 'lib-school-address',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatInputModule, MatSelectModule, MatAutocompleteModule],
  templateUrl: './school-address.component.html'
})
export class SchoolAddressComponent implements OnInit {
  @Input() formGroup!: FormGroup;
  @Input() states: State[] = [];
  @Input() onStateChange!: (event: MatSelectChange) => void;
  private readonly countiesService = inject(CountiesService);
  counties: CountyResponse[] = [];
  isNorthCarolina = signal(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filteredStates!: Observable<any[]>;
  filteredCounties!: Observable<CountyResponse[]>;

  ngOnInit() {
    this.filteredStates = this.formGroup.get('schoolState')!.valueChanges.pipe(
      startWith(this.formGroup.get('schoolState')!.value),
      map((value) => this._filterStates(value || ''))
    );

    this.formGroup
      .get('schoolState')!
      .valueChanges.pipe(startWith(this.formGroup.get('schoolState')!.value))
      .subscribe((state) => {
        let stateCode: string | undefined;
        if (typeof state === 'object' && state?.code) {
          stateCode = state.code;
        } else if (typeof state === 'string') {
          const matched = this.states.find((s) => s.code === state || s.name === state);
          stateCode = matched?.code;
        }

        this.isNorthCarolina.set(stateCode === 'NC');
        if (this.isNorthCarolina() && this.counties.length === 0) {
          this.loadCounties('NC');
        }

        if (!this.isNorthCarolina()) {
          this.formGroup.patchValue({ schoolCounty: '' }, { emitEvent: true });
        }
        if (this.isNorthCarolina()) {
          setTimeout(() => {
            this.formGroup.get('schoolCounty')?.updateValueAndValidity({ emitEvent: true });
          });
        }
      });

    this.filteredCounties = this.formGroup.get('schoolCounty')!.valueChanges.pipe(
      startWith(this.formGroup.get('schoolCounty')!.value || ''),
      map((value) => this._filterCounties(value || ''))
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _filterStates(value: any): any[] {
    // Handle both string and object values
    const filterValue = typeof value === 'string' ? value.toLowerCase() : value?.name?.toLowerCase() || '';
    return this.states.filter((state) => state.name.toLowerCase().includes(filterValue));
  }

  private _filterCounties(value: string): CountyResponse[] {
    const filterValue = value.toLowerCase();
    return this.counties.filter((county) => county.name.toLowerCase().includes(filterValue));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _isNorthCarolina(state: any): boolean {
    const stateName = typeof state === 'string' ? state : state?.name || '';
    return stateName.toLowerCase() === 'north carolina';
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  displayStateName(state: any): string {
    return state?.name || '';
  }

  private loadCounties(state: string): void {
    this.countiesService.getCountiesByState(state).subscribe({
      next: (data: CountyResponse[]) => {
        this.counties = data;

        this.formGroup.get('schoolCounty')!.updateValueAndValidity({ emitEvent: true });
      },
      error: () => {
        this.counties = [];
      }
    });
  }
}
